import { Markdown } from "@client/components/Markdown";
import { useT } from "@client/i18n";
import type { PrepLecture } from "@content/prepSchema";
import { formatAnchor } from "../anchors";
import { AnchorLine } from "./AnchorLine";
import { BookBox } from "./BookBox";
import { MemoDetails } from "./MemoDetails";
import { Panel } from "./Panel";
import { ShowInModelButton } from "./ShowInModel";

export function ProblemCards({
  problems,
  openPanels,
  onTogglePanel,
  onShow,
  className,
}: {
  problems: PrepLecture["problems"];
  openPanels: ReadonlySet<string>;
  onTogglePanel(id: string): void;
  onShow(presetId: string): void;
  className?: string;
}) {
  const t = useT();
  return (
    <Panel title={t("teacher.prep.panel.problems")} {...(className ? { className } : {})}>
      <BookBox>{`${t("teacher.prep.calibrated")} ${problems.flatMap((p) => p.book.map(formatAnchor)).join("; ")}`}</BookBox>
      <ul className="space-y-3">
        {problems.map((problem) => (
          <li key={problem.id} className="rounded-lg border border-ink-200 p-3 dark:border-ink-700">
            <MemoDetails
              id={`problem:${problem.id}`}
              summary={problem.title}
              open={openPanels}
              onToggle={onTogglePanel}
            >
              <Markdown variant="compact">{problem.statement}</Markdown>
              <ShowInModelButton
                preset={problem.preset}
                label={t("teacher.prep.loadProblem")}
                onShow={onShow}
              />
              {problem.variants && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-ink-900 dark:text-ink-50">
                    {t("teacher.prep.variants")}
                  </h3>
                  <ul className="space-y-2">
                    {problem.variants.map((variant) => (
                      <li key={variant.label} className="text-sm">
                        <strong>{variant.label}</strong>
                        <Markdown variant="compact">{variant.statement}</Markdown>
                        <Markdown variant="compact">{`${t("teacher.prep.answer")} ${variant.answer}`}</Markdown>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <MemoDetails
                id={`solution:${problem.id}`}
                summary={t("teacher.prep.solution")}
                open={openPanels}
                onToggle={onTogglePanel}
              >
                <Markdown>{problem.solution}</Markdown>
              </MemoDetails>
              <ul className="space-y-1 text-sm">
                {problem.pitfalls.map((pitfall, index) => (
                  <li key={index}>
                    <strong>{t("teacher.prep.pitfall")}</strong>{" "}
                    <Markdown variant="inline">{pitfall}</Markdown>
                  </li>
                ))}
              </ul>
              <AnchorLine book={problem.book} beyondBook={problem.beyondBook} />
            </MemoDetails>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
